import React from 'react';
import { Check, Minus } from 'lucide-react';

import type { ScopeNamespaceCount } from '@/features/system/orchestrator/api/scopesApi';

export function SearchInput({ value, onChange, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pac-glass/60 rounded px-2 py-1 text-xs text-neutral-200 placeholder-neutral-600 outline-none focus:border-neutral-500 transition-colors"
    />
  );
}

/** Tri-state checkbox indicator. Used at namespace level for bulk selection. */
function TriCheckbox({
  state,
  onClick,
}: {
  state: 'all' | 'partial' | 'none';
  onClick?: (e: React.MouseEvent) => void;
}) {
  const base =
    'shrink-0 w-3.5 h-3.5 rounded-[3px] flex items-center justify-center transition-all duration-150';
  if (state === 'all') {
    return (
      <span
        role="checkbox"
        aria-checked="true"
        className={`${base} bg-emerald-600 border border-emerald-500 cursor-pointer hover:bg-emerald-500`}
        onClick={onClick}
      >
        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
      </span>
    );
  }
  if (state === 'partial') {
    return (
      <span
        role="checkbox"
        aria-checked="mixed"
        className={`${base} bg-amber-600/80 border border-amber-500 cursor-pointer hover:bg-amber-500`}
        onClick={onClick}
      >
        <Minus className="w-2.5 h-2.5 text-white" strokeWidth={3} />
      </span>
    );
  }
  return (
    <span
      role="checkbox"
      aria-checked="false"
      className={`${base} border border-neutral-600 cursor-pointer hover:border-neutral-400`}
      onClick={onClick}
    />
  );
}

export default function NamespaceList({
  namespaces,
  selectedNamespace,
  onSelect,
  searchValue,
  onSearchChange,
  loading,
  title = 'Scope Namespaces',
  selectionState,
  onToggleSelection,
  selectAllState,
  onSelectAll,
  loadingNamespaces,
}: {
  namespaces: ScopeNamespaceCount[];
  selectedNamespace: string | null;
  onSelect: (ns: string | null) => void;
  searchValue: string;
  onSearchChange: (v: string) => void;
  loading?: boolean;
  title?: string;
  /** Per-namespace tri-state: 'all' / 'partial' / 'none'. Omit to hide checkboxes. */
  selectionState?: Map<string, 'all' | 'partial' | 'none'>;
  /** Called when the tri-state checkbox on a namespace row is clicked. */
  onToggleSelection?: (ns: string) => void;
  /** Tri-state for the header-level "select all" checkbox. */
  selectAllState?: 'all' | 'partial' | 'none';
  /** Called when the header "select all" checkbox is clicked. */
  onSelectAll?: () => void;
  /** Namespaces currently being fetched for selection. Shows loading pulse. */
  loadingNamespaces?: Set<string>;
}) {
  const hasSelection = !!selectionState && !!onToggleSelection;
  const hasSelectAll = !!onSelectAll && selectAllState != null;

  return (
    <div className="min-h-0 flex flex-col border-r border-neutral-800 rounded-md">
      <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between gap-2">
        {/* ── Header select-all checkbox ── */}
        {hasSelectAll && (
          <TriCheckbox state={selectAllState!} onClick={(e) => { e.stopPropagation(); onSelectAll!(); }} />
        )}
        <span className="text-sm text-neutral-200 flex-1 min-w-0 truncate">{title}</span>
        <span className="text-xs text-neutral-500 shrink-0">{namespaces.length}</span>
      </div>

      <div className="px-3 py-2 border-b border-neutral-800/50">
        <SearchInput value={searchValue} onChange={onSearchChange} placeholder="Filter namespaces…" />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading && <div className="p-2 text-[11px] text-neutral-500">Loading…</div>}
        {namespaces.length === 0 && !loading && (
          <div className="p-3 text-sm text-neutral-500">{searchValue ? 'No matching namespaces.' : 'No scopes found.'}</div>
        )}

        {namespaces.map((n) => {
          const active = n.scope_namespace === selectedNamespace;
          const state = selectionState?.get(n.scope_namespace) ?? 'none';
          const isLoading = loadingNamespaces?.has(n.scope_namespace);

          return (
            <button
              key={n.scope_namespace}
              type="button"
              onClick={() => onSelect(active ? null : n.scope_namespace)}
              className={
                'w-full text-left px-3 py-2 text-xs border-b border-neutral-800/50 ' +
                'flex items-center gap-2 transition-colors cursor-default group ' +
                (active
                  ? 'pac-glass text-neutral-100'
                  : 'text-neutral-400 hover:bg-black hover:text-neutral-200')
              }
            >
              {/* ── Tri-state checkbox ── */}
              {hasSelection && (
                isLoading ? (
                  <span className="shrink-0 w-3.5 h-3.5 rounded-[3px] border border-neutral-600 animate-pulse bg-neutral-700/50" />
                ) : (
                  <TriCheckbox
                    state={state}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSelection!(n.scope_namespace);
                    }}
                  />
                )
              )}

              <span className="truncate flex-1 min-w-0">{n.scope_namespace}</span>

              {/* ── Selection badge (when partially or fully selected) ── */}
              {hasSelection && state !== 'none' && (
                <span
                  className={
                    'shrink-0 rounded-full w-1.5 h-1.5 ' +
                    (state === 'all' ? 'bg-emerald-400' : 'bg-amber-400')
                  }
                  title={state === 'all' ? 'All keys selected' : 'Some keys selected'}
                />
              )}

              <span
                className={
                  'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] leading-none ' +
                  (active ? 'bg-emerald-700 text-white' : 'pac-glass-subtle text-neutral-500')
                }
              >
                {n.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
